// LandingPage.js - Main landing page for Travel+ CRM with Gradient Hero Design
import React, { useState } from 'react';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
  const [showJoinDropdown, setShowJoinDropdown] = useState(false);

  const agencies = [
    { code: 'MST', name: 'MainStreet Travel Agency', color: 'blue' },
    { code: 'YETI', name: 'Yeti Travel Co', color: 'green' },
    { code: 'BWK', name: 'Boardwalk Travel', color: 'purple' }
  ];

  const handleAgencyLogin = () => {
    if (selectedAgency === 'independent') {
      window.location.href = '/independent';
    } else if (selectedAgency) {
      window.location.href = `/${selectedAgency.toLowerCase()}/login`;
    }
  };

  const handleJoinAgency = (agencyCode) => {
    if (agencyCode === 'independent') {
      window.location.href = '/register/independent';
    } else if (agencyCode === 'agency') {
      window.location.href = '/join-as-agency';
    } else {
      window.location.href = `/${agencyCode.toLowerCase()}/register`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800">
      {/* Header with Logo */}
      <header className="relative z-10 bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-start">
            <div className="flex items-center">
              <img 
                src="/uploads/logos/Travel+ CRM Logo.png" 
                alt="Travel+ CRM Logo" 
                className="h-32 w-auto"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center" style={{display: 'none'}}>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2 mr-3">
                  <span className="text-white font-bold text-2xl">T+</span>
                </div>
                <h1 className="text-3xl font-bold text-white">Travel+ CRM</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-7xl w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 items-start">
            
            {/* Features Card - Desktop Left, Mobile Second */}
            <div className="order-2 lg:order-1 lg:col-span-3">
              <div className="p-4 lg:p-6 h-full">
                <div className="text-center lg:text-left">
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                    Everything You Need to
                    <span className="block text-white">
                      Grow Your Travel Business
                    </span>
                  </h2>
                  <p className="text-lg text-white/90 mb-6 leading-relaxed">
                    Join thousands of travel professionals who trust Travel+ CRM to streamline their operations and maximize their success.
                  </p>
                </div>

                {/* Feature Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Create Travel Quotes</h3>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Client Management</h3>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Collect Payments</h3>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Track Commissions</h3>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Create a Website</h3>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-red-500 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">View Agency Analytics</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Login Card - Desktop Right, Mobile First */}
            <div className="order-1 lg:order-2 lg:col-span-2">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 h-full">
                {/* Tabs */}
                <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
                  <button
                    onClick={() => setActiveTab('login')}
                    className={`flex-1 py-2 px-4 text-center font-medium rounded-md transition-all ${
                      activeTab === 'login'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Login
                  </button>
                  <button
                    onClick={() => setActiveTab('join')}
                    className={`flex-1 py-2 px-4 text-center font-medium rounded-md transition-all ${
                      activeTab === 'join'
                        ? 'bg-white text-green-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Join
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'login' ? (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h3>
                      <p className="text-gray-600">Select your agency to continue</p>
                    </div>
                    
                    {/* Agency Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Choose your agency:
                      </label>
                      <select
                        value={selectedAgency}
                        onChange={(e) => setSelectedAgency(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">-- Select Agency --</option>
                        <option value="independent">Independent Agent</option>
                        <optgroup label="Partner Agencies">
                          {agencies.map((agency) => (
                            <option key={agency.code} value={agency.code}>
                              {agency.name}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </div>

                    {/* Login Button */}
                    <button
                      onClick={handleAgencyLogin}
                      disabled={!selectedAgency}
                      className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      Continue to Login
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Join Travel+ CRM</h3>
                      <p className="text-gray-600">Choose how you'd like to get started</p>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Join Under Agency */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                          <h4 className="font-semibold text-gray-900">Join an Agency</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Apply to join one of our partner agencies</p>
                        <div className="relative">
                          <button
                            onClick={() => setShowJoinDropdown(!showJoinDropdown)}
                            className="w-full py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 text-left"
                          >
                            Select Agency ▼
                          </button>
                          {showJoinDropdown && (
                            <div className="absolute z-20 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200">
                              {agencies.map((agency) => (
                                <button
                                  key={agency.code}
                                  onClick={() => {
                                    handleJoinAgency(agency.code);
                                    setShowJoinDropdown(false);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  {agency.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Join as Independent */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <h4 className="font-semibold text-gray-900">Independent Agent</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Start your own travel business</p>
                        <button
                          onClick={() => handleJoinAgency('independent')}
                          className="w-full py-2 px-3 border border-transparent rounded-md text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                        >
                          Join as Independent
                        </button>
                      </div>

                      {/* Join as Agency */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <h4 className="font-semibold text-gray-900">Join as Agency</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Manage agents with Travel+ CRM</p>
                        <button
                          onClick={() => handleJoinAgency('agency')}
                          className="w-full py-2 px-3 border border-transparent rounded-md text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                        >
                          Learn More
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-white/10 backdrop-blur-sm border-t border-white/20">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-white/80 mb-2">
              © 2025 Northridge Ventures, LLC. All Rights Reserved
            </p>
            <div className="flex justify-center space-x-4">
              <a 
                href="/terms-and-conditions" 
                className="text-sm text-white/60 hover:text-white/80 transition-colors underline"
              >
                Terms and Conditions
              </a>
              <span className="text-white/40">|</span>
              <a 
                href="/privacy-policy" 
                className="text-sm text-white/60 hover:text-white/80 transition-colors underline"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
